import { useLocation } from 'react-router';

import get from 'lodash/get';
import PropTypes from 'prop-types';

import { useTheme } from '@mui/material/styles';

import { useTransl } from 'shared/lib/i18n';

import AsyncTableComponent from '../Table/AsyncTableContainer';

import CheckmarkOrXComponent from '../Table/CheckmarkOrXComponent';

import { formatDate, openUrlInNewTab } from '../../utils';
import { getExportTitle } from '../ViewExport/utils';

import { TABLE_CONSTANTS, TABLE_ID } from '../Table/constants';

import useBreakpoints from '../../hooks/useBreakpoints';
import { DATE_RANGES_TYPES, TABLE_DATA_TYPES } from '../../staticData';
import { history } from '../../store';
import { ButtonLink } from '../Button';
import ListActions from '../Table/ListActions';
import { accessCardsBatchFilter, limitFilter } from '../Table/filters';
import AccessCardsListExport from './AccessCardsListExport';

const AccessCardsListComponent = ({
  items,
  onLoad,
  onRemove,
  getItems,
  listParams,
  listKey,
  batchOptions,
  onExport,
}) => {
  const { t } = useTransl();
  const theme = useTheme();
  const location = useLocation();
  const { isSm } = useBreakpoints();

  const columns = [
    {
      Header: t('title.type'),
      id: 'type',
      sortable: false,
      accessor: ({ type }) => type,
    },
    {
      Header: t('title.batchName'),
      id: 'access_card_batch',
      headerClassName: 'sort',
      accessor: ({ accessCardBatchName }) => accessCardBatchName,
    },
    {
      Header: t('title.number'),
      id: 'number',
      headerClassName: 'sort',
      accessor: ({ number }) => number,
    },
    {
      Header: t('title.code'),
      id: 'scan_code',
      headerClassName: 'sort',
      accessor: ({ scanCode }) => scanCode,
    },
    {
      Header: t('title.effectiveDate'),
      id: 'effective_date',
      headerClassName: 'sort',
      accessor: ({ effectiveDate }) => formatDate(effectiveDate),
    },
    {
      Header: t('title.validUntil'),
      id: 'valid_until',
      headerClassName: 'sort',
      accessor: ({ validUntil }) => formatDate(validUntil),
    },
    {
      Header: t('title.person'),
      id: 'person',
      sortable: false,
      accessor: ({ person }) => {
        if (!person) return null;
        const { id, firstName, lastName } = person;
        const name = `${firstName} ${lastName}`;
        const url = `/global-person-search/${id}`;
        return (
          <ButtonLink
            onClick={() => history.push(url)}
            style={{ color: theme.palette.custom.link }}
          >
            {name}
          </ButtonLink>
        );
      },
    },
    {
      Header: t('title.addedToIntercom'),
      id: 'synced_with_acs',
      headerClassName: 'sort',
      accessor: ({ syncedWithAcs }) => (
        <CheckmarkOrXComponent value={syncedWithAcs} />
      ),
    },
    {
      Header: t('title.creator'),
      id: 'creator',
      sortable: false,
      accessor: ({ creator }) => {
        if (!creator) return null;
        const { id, firstName, lastName } = creator;
        const name = `${firstName} ${lastName}`;
        const url = `/global-person-search/${id}`;
        return (
          <ButtonLink
            onClick={() => history.push(url)}
            style={{ color: theme.palette.custom.link }}
          >
            {name}
          </ButtonLink>
        );
      },
    },
    {
      Header: t('title.role'),
      id: 'creator_role',
      sortable: false,
      accessor: ({ creator }) => creator?.roleName ?? t('common.NR'),
    },
    {
      Header: t('title.actions'),
      id: 'actions',
      fixed: 'right',
      sortable: false,
      width: 120,
      accessor: ({ id }) => {
        const removeAccessCard = [
          {
            label: t('actions.remove'),
            color: theme.palette.secondary.main,
            onClick: () => onRemove(id, listParams),
          },
        ];
        return <ListActions actions={removeAccessCard} isFullWidth />;
      },
    },
  ];

  const dateRangeTypeOptions = [
    DATE_RANGES_TYPES.effective_date,
    DATE_RANGES_TYPES.valid_until,
  ];

  const filters = [
    limitFilter(listParams),
    accessCardsBatchFilter(listParams, batchOptions),
  ];

  const defaultParams = {
    ordering: '-created_at',
  };

  const onRowClick = (rowInfo, isAltKey) => {
    const id =
      get(rowInfo, 'original.id', null) || get(rowInfo, '_original.id', null);
    if (isAltKey) {
      openUrlInNewTab(`${location.origin}/access-cards/list/${id}`);
    } else {
      id && history.push(`/access-cards/list/${id}`);
    }
  };

  const rowStyles = {
    style: {
      cursor: 'pointer',
    },
  };

  const action = [
    {
      label: isSm ? t('accessCard.add') : `+ ${t('actions.add')}`,
      url: '/access-cards/list/create',
      color: theme.palette.success.main,
    },
  ];

  const tableDataType = TABLE_DATA_TYPES.accessCards;

  const title = t('title.accessCards');
  const exportTitle = getExportTitle(title);
  const exportActions = (
    <AccessCardsListExport
      title={exportTitle}
      getDataForExport={onExport}
      columns={columns}
    />
  );

  return (
    <AsyncTableComponent
      tableId={TABLE_ID.accessCards}
      title={title}
      columns={columns}
      action={<ListActions actions={action} />}
      defaultSort={TABLE_CONSTANTS.created_at}
      defaultSortDesc
      manual
      filters={filters}
      showFiltersAreApplied={false}
      items={items}
      onLoad={onLoad}
      getItems={getItems}
      listParams={listParams}
      defaultParams={defaultParams}
      tableDataType={tableDataType}
      listKey={listKey}
      dateRangeType={DATE_RANGES_TYPES.effective_date}
      dateRangeTypeOptions={dateRangeTypeOptions}
      onRowClick={onRowClick}
      rowStyles={rowStyles}
      datePicker
      exportActions={exportActions}
    />
  );
};

AccessCardsListComponent.propTypes = {
  items: PropTypes.instanceOf(Object).isRequired,
  onLoad: PropTypes.func.isRequired,
  getItems: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  batchOptions: PropTypes.instanceOf(Array).isRequired,
  listParams: PropTypes.instanceOf(Object).isRequired,
  listKey: PropTypes.string.isRequired,
};

export default AccessCardsListComponent;
